import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, NgStyle } from '@angular/common';

const COLS = 6;
const ROWS = 5;

interface Tile {
  col: number;
  row: number;
  dx: string;
  dy: string;
  rotate: string;
  delay: string;
}

@Component({
  selector: 'app-dream-team',
  standalone: true,
  imports: [NgStyle],
  templateUrl: './dream-team.html',
  styleUrl: './dream-team.css'
})
export class DreamTeamComponent implements OnInit, OnDestroy {
  @ViewChild('sectionEl', { static: true }) sectionEl!: ElementRef<HTMLElement>;

  private platformId = inject(PLATFORM_ID);
  private observer: IntersectionObserver | null = null;

  assembled = false;
  tiles: Tile[] = [];

  ngOnInit() {
    // Seeded-random so tiles are consistent but look scattered
    const rng = (seed: number) => {
      const x = Math.sin(seed + 1) * 10000;
      return x - Math.floor(x);
    };

    let i = 0;
    for (let row = 0; row < ROWS; row++) {
      for (let col = 0; col < COLS; col++) {
        const r = rng(i++);
        const dx = (r * 160 - 80).toFixed(1) + 'px';
        const dy = (rng(i++) * 140 - 70).toFixed(1) + 'px';
        const rotate = (rng(i++) * 24 - 12).toFixed(2) + 'deg';
        const delay = (rng(i++) * 0.55).toFixed(3) + 's';
        this.tiles.push({ col, row, dx, dy, rotate, delay });
      }
    }

    if (!isPlatformBrowser(this.platformId)) return;

    this.observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) this.assembled = true; },
      { threshold: 0.18 }
    );
    this.observer.observe(this.sectionEl.nativeElement);
  }

  ngOnDestroy() {
    this.observer?.disconnect();
  }

  tileStyle(tile: Tile) {
    const wPct = 100 / COLS;
    const hPct = 100 / ROWS;
    return {
      width: wPct + '%',
      height: hPct + '%',
      left: tile.col * wPct + '%',
      top: tile.row * hPct + '%',
      'background-position': `${(tile.col / (COLS - 1)) * 100}% ${(tile.row / (ROWS - 1)) * 100}%`,
      'background-size': `${COLS * 100}% ${ROWS * 100}%`,
      '--dx': tile.dx,
      '--dy': tile.dy,
      '--rot': tile.rotate,
      'transition-delay': this.assembled ? tile.delay : '0s',
    };
  }
}
